import { z } from "zod";

/**
 * URL search params for `/$workspaceSlug/payroll/payslips`.
 *
 * TWO LENSES, both the reader's own: `mine` (the payslips that were issued)
 * and `salary` (the CTC they were issued against, CTC redesign). There is
 * still no admin lens here, because the admin view of somebody else's pay is
 * a different page with a different DTO (HRMS-PAY-024 for slips, the Salaries
 * grid for CTC). Any other `scope` is normalized: a stale bookmark or a
 * hand-typed `?scope=workspace` lands on the personal lens rather than an
 * error card (FR-73).
 *
 * It is `z.string()` and not `z.enum([...]).catch("mine")` on purpose. `.catch`
 * would silently rewrite the PARSED value while leaving the URL saying
 * `workspace`, so the address bar and the page would disagree for the rest of
 * the session. Keeping the raw value lets the route body see the mismatch and
 * clean the URL up.
 *
 * `slip` is the notification deep link (FR-73): the row it names opens in the
 * detail sheet. `year` is the picked payroll year. `moneyPeriod` is the payroll
 * period the tax and benefit section below the slips is about (HRMS-PAY-032).
 * All three in the URL rather than `useState` (S3) so an open payslip, or a
 * period somebody is asking a question about, is a link they can send.
 *
 * No amount is ever a search param (values checklist): the URL is the one place
 * a payslip figure would end up in a browser history, a referrer header, and
 * somebody else's screen share. A period id is an opaque identifier and carries
 * nothing about the person or the money (FE-SEC7).
 */

export const MY_PAYSLIPS_SCOPES = ["mine", "salary"] as const;
export type MyPayslipsScope = (typeof MY_PAYSLIPS_SCOPES)[number];

export const MY_PAYSLIPS_DEFAULT_SCOPE: MyPayslipsScope = "mine";

export const myPayslipsRouteSearchSchema = z
  .object({
    scope: z.string().optional(),
    slip: z.string().optional(),
    /// Four digits, matching the `YYYY` prefix of a slip's `periodStart`.
    year: z.string().optional(),
    /// A payroll period id. Absent means "the first period on offer", which is
    /// what an unparameterised visit should land on.
    moneyPeriod: z.string().optional(),
  })
  .strict();

export type MyPayslipsRouteSearch = z.infer<typeof myPayslipsRouteSearchSchema>;

/**
 * Whether `?scope=` names a lens this page does not have. Absent is fine: the
 * bare URL is the personal lens, and forcing `?scope=mine` into every link
 * would put a redundant param in every notification.
 */
export function isForbiddenPayslipScope(scope: string | undefined): boolean {
  if (scope === undefined) return false;
  return !MY_PAYSLIPS_SCOPES.includes(scope as MyPayslipsScope);
}

/**
 * The lens to render. Absent and forbidden both resolve to `mine`; the route
 * still cleans a forbidden value out of the URL, this only decides what to
 * show while it does.
 */
export function resolveMyPayslipsScope(scope: string | undefined): MyPayslipsScope {
  if (scope === undefined || isForbiddenPayslipScope(scope)) return MY_PAYSLIPS_DEFAULT_SCOPE;
  // reason: narrowed by isForbiddenPayslipScope on the line above.
  return scope as MyPayslipsScope;
}
